import { useState, useEffect, useCallback } from "react";
import api, { fmtMoney, errDetail, BACKEND_URL } from "../lib/api";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Search, Plus, Minus, Trash2, ShoppingCart, Package, ImageOff } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { value: "nakit", label: "Nakit" },
  { value: "kredi_karti", label: "Kredi Kartı" },
  { value: "havale", label: "Havale" },
  { value: "karma", label: "Karma Ödeme" },
];

export default function POS() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ search: "", category_id: "", subcategory_id: "", brand: "" });
  const [cart, setCart] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState("nakit");
  const [discountType, setDiscountType] = useState("yok");
  const [discountValue, setDiscountValue] = useState("");
  const [payments, setPayments] = useState({ nakit: "", kredi_karti: "", havale: "" });
  const [customerId, setCustomerId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories", { params: { limit: 200 } }).then((r) => setCategories(r.data.items));
    api.get("/subcategories", { params: { limit: 200 } }).then((r) => setSubcategories(r.data.items));
    api.get("/customers", { params: { limit: 200 } }).then((r) => setCustomers(r.data.items)).catch(() => {});
  }, []);

  const loadProducts = useCallback(() => {
    api.get("/pos/products", { params: filters }).then((r) => setProducts(r.data.items)).catch((e) => toast.error(errDetail(e)));
  }, [filters]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const brands = [...new Set(products.map((p) => p.brand).filter(Boolean))];

  const addToCart = (p) => {
    if ((p.stock || 0) <= 0) { toast.error("Stokta yok"); return; }
    setCart((c) => {
      const found = c.find((i) => i.product_id === p.id);
      if (found) {
        if (found.quantity + 1 > p.stock) { toast.error("Yetersiz stok"); return c; }
        return c.map((i) => (i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...c, { product_id: p.id, name: p.name, price: p.sale_price, quantity: 1, stock: p.stock }];
    });
  };

  const changeQty = (id, delta) =>
    setCart((c) => c
      .map((i) => (i.product_id === id ? { ...i, quantity: Math.min(Math.max(i.quantity + delta, 0), i.stock) } : i))
      .filter((i) => i.quantity > 0));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const discountAmount =
    discountType === "yuzde" ? (subtotal * Math.min(Number(discountValue) || 0, 100)) / 100
      : discountType === "tutar" ? Math.min(Number(discountValue) || 0, subtotal)
      : 0;
  const total = Math.round((subtotal - discountAmount) * 100) / 100;
  const karmaSum = Object.values(payments).reduce((s, v) => s + (Number(v) || 0), 0);

  const completeSale = async () => {
    setSaving(true);
    try {
      const payload = {
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        payment_method: method,
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        customer_id: customerId || "",
        customer_name: customers.find((c) => c.id === customerId)?.name || "",
      };
      if (method === "karma")
        payload.payments = { nakit: Number(payments.nakit) || 0, kredi_karti: Number(payments.kredi_karti) || 0, havale: Number(payments.havale) || 0 };
      const { data } = await api.post("/sales", payload);
      toast.success(`Satış tamamlandı: ${data.sale_no} · ${fmtMoney(data.total)}`);
      setCart([]); setPayOpen(false); setDiscountType("yok"); setDiscountValue("");
      setPayments({ nakit: "", kredi_karti: "", havale: "" }); setCustomerId("");
      loadProducts();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="pos-page">
      <PageHeader title="Satış Ekranı" subtitle="Ürün seçerek hızlı satış yapın" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="relative col-span-2 md:col-span-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="pos-search-input" className="rounded-none pl-9" placeholder="Ürün ara..."
                value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
            </div>
            <Select value={filters.category_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, category_id: v === "all" ? "" : v, subcategory_id: "" })}>
              <SelectTrigger data-testid="pos-category-filter" className="rounded-none"><SelectValue placeholder="Ana Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.subcategory_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, subcategory_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="pos-subcategory-filter" className="rounded-none"><SelectValue placeholder="Alt Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Alt Kategoriler</SelectItem>
                {subcategories.filter((s) => !filters.category_id || s.category_id === filters.category_id)
                  .map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.brand || "all"} onValueChange={(v) => setFilters({ ...filters, brand: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="pos-brand-filter" className="rounded-none"><SelectValue placeholder="Marka" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Markalar</SelectItem>
                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4" data-testid="pos-product-grid">
            {products.length === 0 && (
              <p className="col-span-full py-10 text-center text-sm text-gray-400">Ürün bulunamadı</p>
            )}
            {products.map((p) => (
              <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addToCart(p)}
                className="border border-gray-200 bg-white text-left transition-colors duration-200 hover:border-[#D4AF37]">
                <div className="flex aspect-square w-full items-center justify-center overflow-hidden bg-gray-50">
                  {p.image ? (
                    <img src={`${BACKEND_URL}${p.image}`} alt={p.name} className="h-full w-full object-contain" />
                  ) : (
                    <ImageOff size={28} className="text-gray-300" />
                  )}
                </div>
                <div className="p-3">
                  <p className="truncate text-sm font-medium text-[#0B1F3A]">{p.name}</p>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="font-heading text-sm font-semibold text-[#D4AF37]">{fmtMoney(p.sale_price)}</span>
                    <span className={`text-xs ${p.stock <= (p.min_stock || 0) ? "text-red-500" : "text-gray-400"}`}>
                      Stok: {p.stock}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-0 border border-gray-200 bg-white" data-testid="pos-cart">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <h3 className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wider">
                <ShoppingCart size={18} /> Sepet
              </h3>
              <span className="text-xs text-gray-500">{cart.length} kalem</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {cart.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-10 text-gray-300">
                  <Package size={32} strokeWidth={1.5} />
                  <p className="text-sm">Sepet boş</p>
                </div>
              )}
              {cart.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{i.name}</p>
                    <p className="text-xs text-gray-400">{fmtMoney(i.price)} / adet</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`cart-decrease-${i.product_id}`} onClick={() => changeQty(i.product_id, -1)}
                      className="flex h-7 w-7 items-center justify-center border border-gray-200 hover:border-[#D4AF37]"><Minus size={12} /></button>
                    <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${i.product_id}`}>{i.quantity}</span>
                    <button data-testid={`cart-increase-${i.product_id}`} onClick={() => changeQty(i.product_id, 1)}
                      className="flex h-7 w-7 items-center justify-center border border-gray-200 hover:border-[#D4AF37]"><Plus size={12} /></button>
                  </div>
                  <p className="w-20 text-right text-sm font-semibold">{fmtMoney(i.price * i.quantity)}</p>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => setCart((c) => c.filter((x) => x.product_id !== i.product_id))}
                    className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-gray-200 p-5">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Ara Toplam</span><span data-testid="cart-subtotal">{fmtMoney(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-red-500">
                  <span>İndirim</span><span>-{fmtMoney(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-2 font-heading text-lg font-semibold">
                <span>Toplam</span><span className="text-[#D4AF37]" data-testid="cart-total">{fmtMoney(total)}</span>
              </div>
              <Button data-testid="pos-checkout-button" disabled={cart.length === 0} onClick={() => setPayOpen(true)}
                className="mt-2 h-11 w-full rounded-none bg-[#D4AF37] font-semibold text-[#0B1F3A] hover:bg-[#c6a230]">
                Ödemeye Geç
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Ödeme</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Ödeme Yöntemi</Label>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map((m) => (
                  <button key={m.value} data-testid={`payment-method-${m.value}`} onClick={() => setMethod(m.value)}
                    className={`border px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                      method === m.value ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B1F3A]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {method === "karma" && (
              <div className="grid grid-cols-3 gap-2">
                {["nakit", "kredi_karti", "havale"].map((k) => (
                  <div key={k}>
                    <Label className="mb-1 block text-xs text-gray-500">{METHODS.find((m) => m.value === k).label}</Label>
                    <Input data-testid={`karma-${k}`} type="number" className="rounded-none" value={payments[k]}
                      onChange={(e) => setPayments({ ...payments, [k]: e.target.value })} />
                  </div>
                ))}
                <p className={`col-span-3 text-xs ${Math.abs(karmaSum - total) < 0.01 ? "text-emerald-600" : "text-red-500"}`}>
                  Girilen: {fmtMoney(karmaSum)} / Gereken: {fmtMoney(total)}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">İndirim Türü</Label>
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger data-testid="discount-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yok">İndirim Yok</SelectItem>
                    <SelectItem value="yuzde">Yüzde (%)</SelectItem>
                    <SelectItem value="tutar">Sabit Tutar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">İndirim Değeri</Label>
                <Input data-testid="discount-value-input" type="number" className="rounded-none" disabled={discountType === "yok"}
                  value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Müşteri (opsiyonel)</Label>
              <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sale-customer-select" className="rounded-none"><SelectValue placeholder="Müşteri seç" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Müşteri yok</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <span className="font-heading text-lg font-semibold">Toplam: <span className="text-[#D4AF37]">{fmtMoney(total)}</span></span>
              <Button data-testid="complete-sale-button" disabled={saving || (method === "karma" && Math.abs(karmaSum - total) > 0.01)}
                onClick={completeSale} className="rounded-none bg-[#0B1F3A] px-6 text-white hover:bg-[#132c4f]">
                {saving ? "İşleniyor..." : "Satışı Tamamla"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
